// ── Public types ──────────────────────────────────────────────────────────────

export interface AudioLoaderConfig {
  /** AudioContext used for decoding. Caller (Plan 20) creates + owns the context. */
  readonly audioContext: AudioContext;
}

export interface AudioAssetLoader {
  /** Fetches a same-origin audio file (.opus/.mp3/.wav) and decodes into AudioBuffer
   *  via audioContext.decodeAudioData. Resolves to AudioBuffer.
   *  Rejects with AudioLoaderError on fetch/decode failure. */
  load(url: string): Promise<AudioBuffer>;
  /** Build a short silent AudioBuffer for placeholderSilence entries
   *  (per external-api-fallbacks.md §ElevenLabs). */
  createSilentBuffer(durationSeconds?: number): AudioBuffer;
}

export class AudioLoaderError extends Error {
  constructor(
    public readonly reason: 'fetch_failed' | 'decode_failed' | 'context_closed',
    public readonly detail: string,
    public readonly url: string,
  ) {
    super(`AudioLoaderError(${reason}, ${url}): ${detail}`);
    this.name = 'AudioLoaderError';
  }
}

// ── Factory ───────────────────────────────────────────────────────────────────

/** Factory. Constructs an AudioAssetLoader bound to the given config. */
export function createAudioAssetLoader(config: AudioLoaderConfig): AudioAssetLoader {
  return {
    async load(url: string): Promise<AudioBuffer> {
      // Step 1: check for closed context BEFORE any fetch (prevent wasted network calls)
      if (config.audioContext.state === 'closed') {
        return Promise.reject(
          new AudioLoaderError('context_closed', 'AudioContext is closed', url),
        );
      }

      // Step 2: fetch the audio file
      let res: Response;
      try {
        res = await fetch(url);
      } catch (err: unknown) {
        const detail = err instanceof Error ? err.message : String(err);
        return Promise.reject(new AudioLoaderError('fetch_failed', detail, url));
      }

      if (!res.ok) {
        return Promise.reject(
          new AudioLoaderError('fetch_failed', `HTTP ${res.status}`, url),
        );
      }

      // Step 3: read ArrayBuffer from response
      const arrayBuffer = await res.arrayBuffer();

      // Step 4: decode into AudioBuffer
      try {
        const audioBuffer = await config.audioContext.decodeAudioData(arrayBuffer);
        return audioBuffer;
      } catch (err: unknown) {
        const detail = err instanceof Error ? err.message : String(err);
        return Promise.reject(new AudioLoaderError('decode_failed', detail, url));
      }
    },

    createSilentBuffer(durationSeconds = 0.1): AudioBuffer {
      const frames = Math.round(durationSeconds * config.audioContext.sampleRate);
      // createBuffer returns a zero-initialized buffer — no need to explicitly silence
      return config.audioContext.createBuffer(1, frames, config.audioContext.sampleRate);
    },
  };
}
